import { useEffect } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";

import PurchaseBillItemsTable from "./PurchaseBillItemsTable";
import { PurchaseBill, PurchaseBillPayload, SelectOption } from "../types";

interface PurchaseBillFormDialogProps {
  open: boolean;
  loading?: boolean;
  purchaseBill?: PurchaseBill | null;
  suppliers: SelectOption[];
  spareParts: SelectOption[];
  hubs: SelectOption[];
  onClose: () => void;
  onSubmit: (payload: PurchaseBillPayload) => Promise<void>;
}

const today = () => new Date().toISOString().split("T")[0];

const defaultValues: PurchaseBillPayload = {
  supplierId: "",
  invoiceNumber: "",
  invoiceDate: today(),
  purchaseDate: today(),
  currency: "",
  warehouseId: "",
  otherCharges: 0,
  remarks: "",
  items: [],
};

const emptyToUndefined = (value?: string) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const toPaise = (value: number) => Math.round(value * 100);

const PurchaseBillFormDialog = ({
  open,
  loading = false,
  purchaseBill,
  suppliers,
  spareParts,
  hubs,
  onClose,
  onSubmit,
}: PurchaseBillFormDialogProps) => {
  const methods = useForm<PurchaseBillPayload>({ defaultValues });
  const { control, handleSubmit, reset, watch } = methods;

  const items = watch("items") || [];
  const otherCharges = Number(watch("otherCharges") || 0);
  const enteredCurrency = watch("currency") ?? "";
  const subtotal = items.reduce(
    (sum, item) => sum + Number(item.quantity || 0) * Number(item.unitPrice || 0),
    0
  );
  const total = subtotal + otherCharges;
  const currencyAmount = Number(enteredCurrency);
  const hasCurrencyAmount = enteredCurrency !== "" && Number.isFinite(currencyAmount);
  const currencyMatchesTotal = hasCurrencyAmount && toPaise(currencyAmount) === toPaise(total);

  useEffect(() => {
    if (purchaseBill) {
      reset({
        supplierId: purchaseBill.supplierId,
        invoiceNumber: purchaseBill.invoiceNumber,
        invoiceDate: purchaseBill.invoiceDate?.split("T")[0],
        purchaseDate: purchaseBill.purchaseDate?.split("T")[0],
        currency: purchaseBill.currency || "",
        warehouseId: purchaseBill.warehouseId || "",
        otherCharges: purchaseBill.otherCharges,
        remarks: purchaseBill.remarks || "",
        items: purchaseBill.items.map((item) => ({
          sparePartId: item.sparePartId,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          remarks: item.remarks || "",
        })),
      });
    } else {
      reset(defaultValues);
    }
  }, [purchaseBill, reset]);

  const submit = (values: PurchaseBillPayload) =>
    onSubmit({
      supplierId: values.supplierId,
      invoiceNumber: values.invoiceNumber.trim(),
      invoiceDate: values.invoiceDate,
      purchaseDate: values.purchaseDate,
      currency: values.currency?.trim(),
      warehouseId: values.warehouseId,
      otherCharges: Number(values.otherCharges || 0),
      remarks: emptyToUndefined(values.remarks),
      // The item table keeps a blank next row ready for fast entry. It is UI-only
      // and must not be included in the saved purchase bill.
      items: values.items
        .filter((item) => item.sparePartId)
        .map((item) => ({
          sparePartId: item.sparePartId,
          quantity: Number(item.quantity),
          unitPrice: Number(item.unitPrice),
          remarks: emptyToUndefined(item.remarks),
        })),
    });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {purchaseBill ? "Edit Purchase Bill" : "Create Purchase Bill"}
      </DialogTitle>

      <DialogContent dividers>
        <FormProvider {...methods}>
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="supplierId"
                  control={control}
                  rules={{ required: "Supplier is required" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      required
                      label="Supplier"
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ||
                        (suppliers.length ? "" : "Create a supplier first.")
                      }
                    >
                      {suppliers.map((supplier) => (
                        <MenuItem key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="invoiceNumber"
                  control={control}
                  rules={{ required: "Invoice number is required" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      required
                      label="Invoice Number"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="warehouseId"
                  control={control}
                  rules={{ required: "Receiving Hub is required" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      required
                      label="Receiving Hub"
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ||
                        (hubs.length ? "" : "Vehicle Service hubs are not loaded.")
                      }
                    >
                      {hubs.map((hub) => (
                        <MenuItem key={hub.id} value={hub.id}>
                          {hub.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="invoiceDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="date"
                      fullWidth
                      label="Invoice Date"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="purchaseDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="date"
                      fullWidth
                      label="Purchase Date"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="currency"
                  control={control}
                  rules={{
                    required: "Currency amount is required",
                    validate: (value) => {
                      const amount = Number(value);
                      if (!Number.isFinite(amount)) return "Enter a valid currency amount";
                      return toPaise(amount) === toPaise(total)
                        ? true
                        : `Currency amount must exactly match the total (${total.toFixed(2)})`;
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      fullWidth
                      required
                      inputProps={{ min: 0, step: "0.01" }}
                      label="Currency Amount"
                      error={!!fieldState.error || (enteredCurrency !== "" && !currencyMatchesTotal)}
                      helperText={
                        fieldState.error?.message ||
                        "Enter the amount paid for this bill. It must match the calculated total."
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="remarks"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      minRows={3}
                      label="Remarks"
                    />
                  )}
                />
              </Grid>
            </Grid>

            <PurchaseBillItemsTable spareParts={spareParts} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Controller
                  name="otherCharges"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} type="number" fullWidth label="Other Charges" />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField label="Subtotal" value={subtotal.toFixed(2)} fullWidth InputProps={{ readOnly: true }} />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField label="Total" value={total.toFixed(2)} fullWidth InputProps={{ readOnly: true }} />
              </Grid>
            </Grid>

            {enteredCurrency !== "" && !currencyMatchesTotal && (
              <Alert severity="warning">
                Currency amount must exactly match the calculated total of {total.toFixed(2)}. Please correct it before continuing.
              </Alert>
            )}
          </Stack>
        </FormProvider>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          Cancel
        </Button>

        <Button variant="contained" onClick={handleSubmit(submit)} disabled={loading || !currencyMatchesTotal}>
          {loading ? "Saving..." : purchaseBill ? "Update Purchase Bill" : "Create Purchase Bill"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchaseBillFormDialog;
